import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import axiosInstance from '../../api/axiosConfig'; // Adjust path as needed
import {
  Box,
  // Typography, // Title is now in parent
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stack,
  InputAdornment,
  Fade,
} from '@mui/material';
import AddCardIcon from '@mui/icons-material/AddCard';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const logger = {
  info: (...args) => console.log('[INFO]', ...args),
  error: (...args) => console.error('[ERROR]', ...args),
};

function AddBalanceForm({ schoolId, onBalanceAdded }) {
  // Form state
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  // API call state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // **NEW: Effect to reset form when schoolId prop changes**
  useEffect(() => {
    logger.info(`AddBalanceForm: Received schoolId prop: ${schoolId} (Type: ${typeof schoolId})`);
    // Clear form fields and messages when the school selection changes
    // This ensures a fresh form for the newly selected school
    setAmount('');
    setDescription('');
    setError(null);
    setSuccessMessage(null);
    setLoading(false); // Reset loading state too
  }, [schoolId]); // Dependency array includes only schoolId

  // --- Validation ---
  const validateForm = useCallback(() => { // Use useCallback for consistency
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      setError('Please enter a valid positive amount.');
      return false;
    }
    // Allow zero amount? If not, keep > 0 check. If yes, use >= 0 but maybe warn?
    // For adding balance, > 0 makes sense.

    if (!description.trim()) {
      setError('Please enter a description for the transaction.');
      return false;
    }
    setError(null); // Clear error if validation passes
    return true;
  }, [amount, description]); // Dependencies for validation


  // --- Form Submission ---
  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    setSuccessMessage(null); // Clear previous success

    logger.info(`AddBalanceForm: handleSubmit triggered for schoolId: ${schoolId}`);

    // Re-validate before submission
    if (!validateForm()) {
      return;
    }

    // Direct check of the prop value received
    if (!schoolId || schoolId <= 0) {
        const msg = `AddBalanceForm: Invalid schoolId prop (${schoolId}). Cannot submit.`;
        setError(msg);
        logger.error(msg);
        return;
    }

    setLoading(true);
    const payload = {
      schoolId: schoolId, // Use the number prop directly
      amount: parseFloat(amount),
      description: description.trim(),
    };

    logger.info('AddBalanceForm: Attempting to add balance with payload:', payload);

    try {
      const response = await axiosInstance.post('/api/account-balance/add', payload);
      const message = response.data || 'Balance added successfully.';
      setSuccessMessage(message);
      setError(null);
      logger.info(`AddBalanceForm: Balance added successfully for schoolId ${schoolId}:`, message);

      // Clear form ONLY on success
      setAmount('');
      setDescription('');

      if (onBalanceAdded) {
        onBalanceAdded(); // Notify parent
      }

    } catch (err) {
      const errMsg = err.response?.data?.message || err.response?.data || err.message || 'An unexpected error occurred.';
      setError(errMsg);
      setSuccessMessage(null);
      logger.error(`AddBalanceForm: Error adding balance for schoolId ${schoolId}:`, errMsg, err.response || err);
    } finally {
      setLoading(false);
    }
    // Dependencies: include all state/props used inside
  }, [amount, description, schoolId, onBalanceAdded, validateForm]);

  return (
    // Removed the outer Paper component, parent handles it
    <Stack spacing={3} component="form" onSubmit={handleSubmit} noValidate>
      {/* Title is now provided by the parent component */}
      {/* <Typography variant="h6" ... /> */}

      {/* Success Message */}
      <Fade in={!!successMessage}>
        <Alert
            severity="success"
            icon={<CheckCircleOutlineIcon fontSize="inherit" />}
            onClose={() => setSuccessMessage(null)} // Allow dismissing
        >
          {successMessage}
        </Alert>
      </Fade>

      {/* Error Message */}
      <Fade in={!!error}>
        <Alert
            severity="error"
            onClose={() => setError(null)} // Allow dismissing
        >
          {error}
        </Alert>
      </Fade>

      {/* Amount Input */}
      <TextField
        label="Amount to Add"
        variant="outlined"
        type="number"
        fullWidth
        required
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start">Credits:</InputAdornment>,
          inputProps: {
            min: 0.01,
            step: 0.01
          }
        }}
        error={!!error && (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0)} // Error if specifically related to amount validation
        helperText={error && (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) ? error : "Enter the number of credits."}
        disabled={loading}
      />

      {/* Description Input */}
      <TextField
        label="Description / Reason"
        variant="outlined"
        fullWidth
        required
        multiline
        rows={2}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="e.g., Manual Top-up, Promo Code"
        error={!!error && !description.trim()} // Error if specifically related to description validation
        helperText={error && !description.trim() ? error : "Brief reason for adding balance."}
        disabled={loading}
      />

      {/* Submit Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          // **REVISED DISABLED LOGIC**: Disable if loading OR if the schoolId prop is invalid
          disabled={loading || !schoolId || schoolId <= 0}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddCardIcon />}
          sx={{ fontWeight: 'bold', px: 3, py: 1.2 }}
        >
          {loading ? 'Adding...' : 'Add Balance'}
        </Button>
      </Box>
    </Stack>
  );
}

AddBalanceForm.propTypes = {
  schoolId: PropTypes.number.isRequired, // Expecting a number now
  onBalanceAdded: PropTypes.func,
};

export default AddBalanceForm;